import React, { useEffect, useState, useCallback } from 'react';
import { categoryAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { FolderOpen, Plus, Edit2 } from 'lucide-react';

export default function CategoriesPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [budgetMap, setBudgetMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', monthlyBudget: '', color: '#6B7280', isRestricted: false });

  const fetchData = useCallback(async () => {
    try {
      const [catRes, budgetRes] = await Promise.all([
        categoryAPI.list(),
        categoryAPI.budgetStatus()
      ]);
      setCategories(catRes.data.categories);
      const map = {};
      budgetRes.data.statuses.forEach(s => { map[s.categoryId] = s; });
      setBudgetMap(map);
    } catch (err) { toast.error('Failed to load categories'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name required'); return; }
    try {
      if (editId) {
        await categoryAPI.update(editId, { ...form, monthlyBudget: parseFloat(form.monthlyBudget) || 0 });
        toast.success('Category updated');
      } else {
        await categoryAPI.create({ ...form, monthlyBudget: parseFloat(form.monthlyBudget) || 0 });
        toast.success('Category created');
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    }
  };

  const startEdit = (cat) => {
    setEditId(cat._id);
    setForm({ name: cat.name, monthlyBudget: cat.monthlyBudget || '', color: cat.color || '#6B7280', isRestricted: cat.isRestricted });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditId(null);
    setForm({ name: '', monthlyBudget: '', color: '#6B7280', isRestricted: false });
    setShowForm(false);
  };

  const isAdmin = user?.role === 'admin';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-4" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-slate-700" />
          <h1 className="text-xl font-bold tracking-tight text-slate-900">Categories & Budgets</h1>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => { resetForm(); setShowForm(!showForm); }} className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="add-category-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Category
          </Button>
        )}
      </div>

      {showForm && isAdmin && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="category-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Name</Label>
              <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="category-name-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Monthly Budget (₹)</Label>
              <Input type="number" value={form.monthlyBudget} onChange={(e) => setForm(f => ({ ...f, monthlyBudget: e.target.value }))} className="mt-1" data-testid="category-budget-input" />
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Color</Label>
              <Input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="mt-1 h-9 p-1" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button type="submit" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white text-xs" data-testid="save-category-btn">{editId ? 'Update' : 'Create'}</Button>
            <Button type="button" size="sm" variant="outline" onClick={resetForm} className="text-xs">Cancel</Button>
          </div>
        </form>
      )}

      {/* Budget cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="budget-cards">
        {categories.map(cat => (
          <BudgetCard key={cat._id} cat={cat} b={budgetMap[cat._id]} isAdmin={isAdmin} onEdit={startEdit} />
        ))}
      </div>
    </div>
  );
}

function alertColor(level) {
  if (level === 'exceeded') return { bar: 'bg-red-500', border: 'border-red-300', pct: 'text-red-600' };
  if (level === 'warning') return { bar: 'bg-amber-500', border: 'border-amber-300', pct: 'text-amber-600' };
  return { bar: 'bg-slate-900', border: 'border-slate-200', pct: 'text-slate-700' };
}

function BudgetCard({ cat, b: rawB, isAdmin, onEdit }) {
  const b = rawB || { spent: 0, budget: cat.monthlyBudget || 0, pct: 0, remaining: cat.monthlyBudget || 0, alertLevel: 'normal', count: 0 };
  const colors = alertColor(b.alertLevel);

  return (
    <div className={`bg-white border rounded-lg p-3 ${colors.border}`} data-testid={`budget-card-${cat.name}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: cat.color || '#6B7280' }} />
          <span className="text-xs font-semibold text-slate-900">{cat.name}</span>
        </div>
        <div className="flex items-center gap-1">
          {b.alertLevel === 'exceeded' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">EXCEEDED</span>}
          {b.alertLevel === 'warning' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">80%+</span>}
          {isAdmin && (
            <button onClick={() => onEdit(cat)} className="p-0.5 hover:bg-slate-100 rounded" data-testid={`edit-category-${cat.name}`}>
              <Edit2 className="w-3 h-3 text-slate-400" />
            </button>
          )}
        </div>
      </div>
      {b.budget > 0 ? (
        <>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-slate-500">₹{b.spent.toLocaleString('en-IN')} of ₹{b.budget.toLocaleString('en-IN')}</span>
            <span className={`font-bold ${colors.pct}`}>{b.pct}%</span>
          </div>
          <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid={`budget-bar-${cat.name}`}>
            <div className={`h-full rounded-full transition-all duration-500 ${colors.bar}`} style={{ width: `${Math.min(b.pct, 100)}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] mt-1">
            <span className="text-slate-400">{b.count} voucher{b.count !== 1 ? 's' : ''}</span>
            <span className="text-slate-400">₹{b.remaining.toLocaleString('en-IN')} left</span>
          </div>
        </>
      ) : (
        <p className="text-[10px] text-slate-400 italic">No budget set</p>
      )}
      {cat.isRestricted && <span className="text-[9px] text-red-500 font-medium mt-1 block">Restricted</span>}
    </div>
  );
}
